import { useEffect, useLayoutEffect } from 'react';

import { useUserPreferences } from '../user-preferences/hooks/useUserPreferences';
import {
  type ColorModePreference,
  isColorModePreference,
  PreferenceScope,
  UserPreferenceKey,
} from '../user-preferences/types';

const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

/**
 * localStorage cache of the resolved theme, read by the first-paint anti-flash
 * script in index.html before React mounts.
 */
const DATA_THEME_STORAGE_KEY = 'revelio-data-theme';

function prefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(DARK_MEDIA_QUERY).matches
  );
}

function resolveColorMode(preference: ColorModePreference): 'light' | 'dark' {
  if (preference === 'system') {
    return prefersDark() ? 'dark' : 'light';
  }
  return preference;
}

/**
 * Writes the `data-theme` marker and caches it for the anti-flash script.
 * `<html>` always carries a value: Chakra declares its :root semantic tokens
 * under `:root[data-theme]`, so dropping it leaves them undefined (white
 * surfaces go transparent, borders go black). `<body>` gates the new-theme
 * tokens.css overrides (scoped to `body[data-theme]`).
 */
function applyTheme(theme: 'light' | 'dark') {
  document.documentElement.setAttribute('data-theme', theme);
  document.documentElement.style.colorScheme = theme;
  document.body.setAttribute('data-theme', theme);
  try {
    localStorage.setItem(DATA_THEME_STORAGE_KEY, theme);
  } catch {
    // localStorage unavailable — the anti-flash script just won't pre-apply.
  }
}

/**
 * Bridges the backend `ColorMode` user preference (System default / Light /
 * Dark) into the `data-theme` marker — the sole dashboard theming mechanism
 * (see tokens.css and the component CSS that keys off `body[data-theme]`).
 * Resolves `system` against `prefers-color-scheme` and follows live OS changes.
 *
 * Always mounted; `ready` carries "Unleash has answered" (flagsReady or
 * flagsError).
 *
 * CRITICAL boot-window guard: this component must WRITE NOTHING until the user
 * preferences have hydrated. Acting earlier would overwrite the marker the
 * index.html anti-flash script just applied, flashing the whole app before
 * snapping back to the saved theme (seen on every homepage refresh). Until
 * both sources have answered, the anti-flash value stays authoritative.
 */
export function ColorModePreferenceSync({ ready }: { ready: boolean }) {
  const { preferences, isInitialLoading } = useUserPreferences(
    PreferenceScope.GLOBAL
  );
  const stored: unknown = preferences[UserPreferenceKey.ColorMode];
  // Anything unrecognised (including a persisted 'legacy' from before dark/light
  // shipped) falls back to following the OS.
  const preference: ColorModePreference = isColorModePreference(stored)
    ? stored
    : 'system';

  useLayoutEffect(() => {
    if (!ready || isInitialLoading) {
      // Flags or preferences still hydrating — leave the anti-flash marker
      // untouched (see the boot-window guard note above).
      return;
    }
    applyTheme(resolveColorMode(preference));
  }, [ready, isInitialLoading, preference]);

  // On unmount fall back to light on BOTH elements rather than dropping the
  // <body> marker: component CSS is scoped under `body[data-theme]`, so removing
  // it leaves those surfaces unstyled. Chakra equally needs the <html> marker
  // for its :root tokens (see applyTheme). Deliberately NOT applyTheme(): that
  // would also overwrite the localStorage cache with 'light', giving a
  // dark-theme user a light first paint on the next load. The cache stays
  // intact so the anti-flash script can restore the saved theme.
  useEffect(() => {
    return () => {
      document.documentElement.setAttribute('data-theme', 'light');
      document.documentElement.style.colorScheme = 'light';
      document.body.setAttribute('data-theme', 'light');
    };
  }, []);

  // While in `system` mode, follow live OS theme changes.
  useEffect(() => {
    if (preference !== 'system') {
      return;
    }
    const mediaQuery = window.matchMedia(DARK_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      applyTheme(event.matches ? 'dark' : 'light');
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [preference]);

  return null;
}
